/**
 * Shared PT Eval & Visit Notes header — Patient / Company / Insurance /
 * Physician / Diagnosis / Medication. Used by all 7 PT note forms.
 */

import { listAgencies } from "../../../../../services/librariesCatalogService";
import { getPatientMedications } from "../../../../../services/patientService";
import { formatDateForInput } from "../../../../../utils/dateUtils";
import {
  buildDiagnosisAllergies,
  buildInsurances,
  buildPhysicians,
} from "../../../patientForm.shared";

export const COMMON_HEADER_GENDER_OPTIONS = [
  "Male",
  "Female",
  "Other",
  "Prefer not to say",
];

const newRowId = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const emptyDiagnosisRow = () => ({
  id: newRowId("dx"),
  diagnosisCode: "",
  diagnosisName: "",
});

export const emptyMedicationRow = () => ({
  id: newRowId("med"),
  medicationName: "",
  dosage: "",
  frequency: "",
});

/** Empty common-header block persisted on every nurse-note payload. */
export const emptyPtNotesCommonHeader = () => ({
  patientName: "",
  dateOfBirth: "",
  gender: "",
  address: "",
  primaryPhone: "",

  companyName: "",
  companyAddress: "",
  companyPhone: "",
  companyFax: "",

  insuranceName: "",
  policyNumber: "",

  physicianName: "",
  physicianNpi: "",
  physicianAddress: "",
  physicianPhone: "",
  physicianFax: "",

  diagnoses: [],
  medications: [],
});

const nonEmpty = (v) => Boolean(String(v ?? "").trim());

const firstNonEmpty = (...candidates) => {
  for (const c of candidates) {
    if (nonEmpty(c)) return String(c).trim();
  }
  return "";
};

/** Format patient street address into a single line. */
export const formatPatientAddressLine = (patient = {}) => {
  const street = [patient.address, patient.addressLine2]
    .filter((p) => nonEmpty(p))
    .join(", ");
  const cityStateZip = [
    patient.city,
    [patient.state, patient.zip].filter((p) => nonEmpty(p)).join(" "),
  ]
    .filter((p) => nonEmpty(p))
    .join(", ");
  return [street, cityStateZip].filter(Boolean).join(", ");
};

export const formatPatientDisplayName = (patient = {}) => {
  const name = [patient.lastName, patient.firstName]
    .filter(Boolean)
    .join(", ");
  return name || "";
};

const formatAgencyAddress = (agency = {}) => {
  const street = agency.address || agency.companyAddress || "";
  const cityStateZip = [
    agency.city,
    [agency.state, agency.zip || agency.zipCode].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return [street, cityStateZip].filter(Boolean).join(", ");
};

/** Normalize diagnosis allergies → addable diagnosis rows. */
export const mapDiagnosesToCommonRows = (patient = {}) => {
  const rows = buildDiagnosisAllergies(patient);
  return rows
    .filter((d) => nonEmpty(d.primaryDiagnosis) || nonEmpty(d.diagnosisCode))
    .map((d) => ({
      id: newRowId("dx"),
      diagnosisCode: d.diagnosisCode || "",
      diagnosisName: d.primaryDiagnosis || "",
    }));
};

/** Map Medication Management rows → common medication table. */
export const mapMedicationsToCommonRows = (medications) => {
  const list = Array.isArray(medications) ? medications : [];
  const active = list.filter(
    (med) => !med?.status || String(med.status).toLowerCase() === "active",
  );
  const source = active.length ? active : list;
  return source.map((med) => {
    const dose = med.dosage || med.dose || "";
    const route = med.route || "";
    return {
      id: med.id != null ? `patient-med-${med.id}` : newRowId("med"),
      medicationName: med.medicationName || "",
      dosage: [dose, route].filter(Boolean).join(" ").trim() || dose,
      frequency: med.frequency || "",
      sourceMedicationId: med.id ?? null,
    };
  });
};

/**
 * Pull legacy flat fields from older nurse-note payloads into the common shape
 * so existing saved forms still render after the shared-header rollout.
 */
export const legacyCommonHeaderFromPayload = (payload = {}) => {
  const p = payload && typeof payload === "object" ? payload : {};
  const ch =
    p.commonHeader && typeof p.commonHeader === "object" ? p.commonHeader : {};

  const diagnosesFromLegacy = () => {
    if (Array.isArray(ch.diagnoses) && ch.diagnoses.length) {
      return ch.diagnoses.map((d) => ({
        id: d.id || newRowId("dx"),
        diagnosisCode: d.diagnosisCode || d.code || "",
        diagnosisName: d.diagnosisName || d.name || d.primaryDiagnosis || "",
      }));
    }
    const rows = [];
    const push = (name, code = "") => {
      if (!nonEmpty(name) && !nonEmpty(code)) return;
      rows.push({
        id: newRowId("dx"),
        diagnosisCode: code || "",
        diagnosisName: name || "",
      });
    };
    push(p.primaryDiagnosis, p.primaryDiagnosisCode);
    push(p.secondaryDiagnosis, p.secondaryDiagnosisCode);
    push(p.primaryDiagnosis1);
    push(p.primaryDiagnosis2);
    push(p.otherDiagnosis1);
    push(p.otherDiagnosis2);
    return rows;
  };

  const medicationsFromLegacy = () => {
    if (Array.isArray(ch.medications) && ch.medications.length) {
      return ch.medications.map((m) => ({
        id: m.id || newRowId("med"),
        medicationName: m.medicationName || m.name || "",
        dosage: m.dosage || m.dosageRoute || "",
        frequency: m.frequency || "",
        sourceMedicationId: m.sourceMedicationId ?? null,
      }));
    }
    if (Array.isArray(p.medications) && p.medications.length) {
      return p.medications.map((m) => ({
        id: m.id || newRowId("med"),
        medicationName: m.medicationName || "",
        dosage: m.dosage || m.dosageRoute || "",
        frequency: m.frequency || "",
        sourceMedicationId: m.sourceMedicationId ?? null,
      }));
    }
    return [];
  };

  return {
    patientName: firstNonEmpty(
      ch.patientName,
      p.patientNameDisplay,
      p.patientName,
      p.fullName,
    ),
    dateOfBirth: firstNonEmpty(ch.dateOfBirth, p.dateOfBirth),
    gender: firstNonEmpty(ch.gender, p.gender),
    address: firstNonEmpty(
      ch.address,
      p.address,
      p.homeAddress,
      [p.address, p.city, p.state, p.zip].filter(Boolean).join(", "),
    ),
    primaryPhone: firstNonEmpty(ch.primaryPhone, p.primaryPhone, p.phone),

    companyName: firstNonEmpty(ch.companyName, p.companyName),
    companyAddress: firstNonEmpty(ch.companyAddress, p.companyAddress),
    companyPhone: firstNonEmpty(ch.companyPhone, p.companyPhone),
    companyFax: firstNonEmpty(ch.companyFax, p.companyFax),

    insuranceName: firstNonEmpty(
      ch.insuranceName,
      p.insuranceName,
      p.primaryPayor,
      p.primaryPayer,
      p.insurance,
      p.primaryCarrier,
    ),
    policyNumber: firstNonEmpty(
      ch.policyNumber,
      p.policyNumber,
      p.primaryPayorIdNumber,
      p.insuranceId,
      p.policyIdNumber,
    ),

    physicianName: firstNonEmpty(
      ch.physicianName,
      p.physicianName,
      p.referringPhysicianName,
    ),
    physicianNpi: firstNonEmpty(
      ch.physicianNpi,
      p.physicianNpi,
      p.referringPhysicianNpi,
    ),
    physicianAddress: firstNonEmpty(
      ch.physicianAddress,
      p.physicianAddress,
      p.referringPhysicianAddress,
    ),
    physicianPhone: firstNonEmpty(
      ch.physicianPhone,
      p.physicianPhone,
      p.referringPhysicianPhone,
    ),
    physicianFax: firstNonEmpty(
      ch.physicianFax,
      p.physicianFax,
      p.referringPhysicianFax,
    ),

    diagnoses: diagnosesFromLegacy(),
    medications: medicationsFromLegacy(),
  };
};

/**
 * Seed wins only where target is blank. Diagnosis/medication arrays: keep
 * target only when it has at least one meaningful row; otherwise take seed.
 */
const hasMeaningfulDiagnoses = (rows) =>
  Array.isArray(rows) &&
  rows.some(
    (d) => nonEmpty(d?.diagnosisCode) || nonEmpty(d?.diagnosisName),
  );

const hasMeaningfulMedications = (rows) =>
  Array.isArray(rows) &&
  rows.some(
    (m) =>
      nonEmpty(m?.medicationName) ||
      nonEmpty(m?.dosage) ||
      nonEmpty(m?.frequency),
  );

export const mergePtNotesCommonHeader = (target = {}, seed = {}) => {
  const base = emptyPtNotesCommonHeader();
  const t = { ...base, ...target };
  const s = { ...base, ...seed };
  const keys = Object.keys(base).filter(
    (k) => k !== "diagnoses" && k !== "medications",
  );
  const out = { ...base };
  keys.forEach((key) => {
    out[key] = firstNonEmpty(t[key], s[key]);
  });
  // Blank placeholder rows must not block chart seed (dx / meds from HPI).
  out.diagnoses = hasMeaningfulDiagnoses(t.diagnoses)
    ? t.diagnoses
    : Array.isArray(s.diagnoses)
      ? s.diagnoses
      : [];
  out.medications = hasMeaningfulMedications(t.medications)
    ? t.medications
    : Array.isArray(s.medications)
      ? s.medications
      : [];
  return out;
};

/** Fetch primary agency profile for company auto-fill (Libraries → Agency). */
export const fetchAgencyCompanyInfo = async () => {
  try {
    const result = await listAgencies({
      status: "Active",
      limit: 1,
      sortBy: "agencyName",
      sortOrder: "asc",
    });
    const item = result?.items?.[0] || null;
    if (!item) return {};
    return {
      companyName: item.agencyName || item.legalName || item.name || "",
      companyAddress: formatAgencyAddress(item),
      companyPhone: item.phone || "",
      companyFax: item.fax || "",
    };
  } catch {
    return {};
  }
};

/** Fetch active chart medications for common med table seed. */
export const fetchChartMedicationsForCommonHeader = async (patientId) => {
  try {
    const res = await getPatientMedications(patientId, { page: 1, limit: 50 });
    const result = res.result || res;
    return mapMedicationsToCommonRows(result.medications || []);
  } catch {
    return [];
  }
};

/**
 * Build a fully seeded common header from patient chart + agency + meds.
 * Does not overwrite caller-provided existing values — use merge after.
 */
export const buildPtNotesCommonHeaderSeed = ({
  patient = {},
  agency = {},
  medications = [],
} = {}) => {
  const insurers = buildInsurances(patient);
  const physicians = buildPhysicians(patient);
  const primaryIns = insurers[0];
  const primaryPhys = physicians[0];

  return {
    ...emptyPtNotesCommonHeader(),
    patientName: formatPatientDisplayName(patient),
    dateOfBirth: patient?.dateOfBirth
      ? formatDateForInput(patient.dateOfBirth)
      : "",
    gender: patient?.gender || "",
    address: formatPatientAddressLine(patient),
    primaryPhone: patient?.phone || "",

    companyName: agency.companyName || "",
    companyAddress: agency.companyAddress || "",
    companyPhone: agency.companyPhone || "",
    companyFax: agency.companyFax || "",

    insuranceName: primaryIns?.insuranceName || "",
    policyNumber: primaryIns?.policyNumber || "",

    physicianName: primaryPhys?.physicianName || "",
    physicianNpi: primaryPhys?.npiNumber || "",
    physicianAddress: primaryPhys?.physicianAddress || "",
    physicianPhone: primaryPhys?.physicianPhone || "",
    physicianFax: primaryPhys?.physicianFax || "",

    diagnoses: mapDiagnosesToCommonRows(patient),
    medications: Array.isArray(medications) ? medications : [],
  };
};

/**
 * Resolve the common header for hydrate: seed chart data, then overlay any
 * saved / legacy payload values (saved wins when non-empty).
 */
export const resolvePtNotesCommonHeader = async ({
  patient,
  patientId,
  payload = {},
}) => {
  const id = patientId ?? patient?.id ?? null;
  const [agency, chartMeds] = await Promise.all([
    fetchAgencyCompanyInfo(),
    id ? fetchChartMedicationsForCommonHeader(id) : Promise.resolve([]),
  ]);
  const seed = buildPtNotesCommonHeaderSeed({
    patient,
    agency,
    medications: chartMeds,
  });
  const legacy = legacyCommonHeaderFromPayload(payload);
  // Saved/legacy values win over fresh chart seed when present.
  return normalizePtCommonHeaderState(
    mergePtNotesCommonHeader(legacy, seed),
  );
};

/**
 * Normalize a commonHeader object for React state / save (arrays always present).
 */
export const normalizePtCommonHeaderState = (header) => {
  const merged = mergePtNotesCommonHeader(
    emptyPtNotesCommonHeader(),
    header && typeof header === "object" ? header : {},
  );
  return {
    ...merged,
    diagnoses: Array.isArray(merged.diagnoses)
      ? merged.diagnoses.map((d) => ({
          id: d.id || `dx-${Math.random().toString(36).slice(2, 9)}`,
          diagnosisCode: d.diagnosisCode || "",
          diagnosisName: d.diagnosisName || "",
        }))
      : [],
    medications: Array.isArray(merged.medications)
      ? merged.medications.map((m) => ({
          id: m.id || `med-${Math.random().toString(36).slice(2, 9)}`,
          medicationName: m.medicationName || "",
          dosage: m.dosage || m.dosageRoute || "",
          frequency: m.frequency || "",
          sourceMedicationId: m.sourceMedicationId ?? null,
        }))
      : [],
  };
};

/** Ensure payload always has a normalized commonHeader before save. */
export const withNormalizedPtCommonHeader = (payload = {}) => {
  const p = payload && typeof payload === "object" ? payload : {};
  const commonHeader = normalizePtCommonHeaderState(
    legacyCommonHeaderFromPayload(p),
  );
  return { ...p, commonHeader };
};
